// Pure functions that turn reviewed scan results into the shape the existing
// Aulas AI hand-off (startAiTestHandoff -> importAiQuestions) already understands.
// Nothing here touches Redux, the network or the DOM, so it is easy to test.

import { figureSrc } from './scanPdfUtils';

export const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

// Parser question types -> the question types the Aulas AI form uses. Anything not
// listed (subjective, other) has no counterpart in the objective test import.
export const AI_TYPE = {
  mcq_single: 'single_mcq',
  mcq_multi: 'multi_correct_mcq',
  numerical: 'integer_type',
  true_false: 'true_false',
  matrix_match: 'matrix_match' // imported as an MCQ with both columns in the statement, as Aulas AI does
};

export const TYPE_LABELS = {
  mcq_single: 'Single correct',
  mcq_multi: 'Multiple correct',
  numerical: 'Numeric',
  true_false: 'True / False',
  subjective: 'Subjective',
  matrix_match: 'Matrix match',
  other: 'Other'
};

// What kind of test a question can go into: objective takes single, multiple, numeric and true/false; subjective
// takes subjective; hybrid (a paper holding both) takes either, each question keeping its own type.
const isWritten = (q) => Boolean(q) && q.question_type === 'subjective';
export const isImportable = (q, kind = 'objective') => {
  if (kind === 'subjective') return isWritten(q);
  if (kind === 'hybrid') return isWritten(q) || Boolean(AI_TYPE[q && q.question_type]);
  return Boolean(AI_TYPE[q && q.question_type]);
};

export const kindOf = (q) => {
  if (q.question_type === 'mcq_multi') return 'multi';
  if (q.question_type === 'numerical') return 'numeric';
  if (q.question_type === 'true_false') return 'tf';
  if (q.question_type === 'subjective') return 'subjective';
  // A matching question with printed answer choices is picked like single correct; without, the matching is typed.
  if (q.question_type === 'matrix_match') return (q.options || []).length ? 'single' : 'matrix';
  return 'single';
};

const hasOptions = (q) => kindOf(q) === 'single' || kindOf(q) === 'multi';

export const emptyAnswer = (q) => (kindOf(q) === 'multi' ? [] : '');

const optionCount = (q) => Math.min((q.options || []).length, LETTERS.length);

// A right answer printed in the PDF is a convenience, never trusted blindly: the
// teacher still sees it selected and can change it.
export const parsePrintedAnswer = (q) => {
  const raw = String(q.answer ?? '').trim();
  if (!raw) return emptyAnswer(q);
  const kind = kindOf(q);
  if (kind === 'subjective') return raw; // the written answer, shown for the teacher to keep or change
  if (kind === 'matrix') return raw; // the matching, e.g. A-q, B-p, C-r
  if (kind === 'tf') {
    const lower = raw.toLowerCase();
    if (/^(t|true|yes)$/.test(lower) || lower.startsWith('true') || raw.startsWith('सत्य') || raw.startsWith('सही')) return 'true';
    if (/^(f|false|no)$/.test(lower) || lower.startsWith('false') || raw.startsWith('असत्य') || raw.startsWith('गलत')) return 'false';
    return '';
  }
  const valid = LETTERS.slice(0, optionCount(q));
  if (kind === 'numeric') {
    const m = raw.match(/-?\d+(\.\d+)?/);
    return m ? m[0] : '';
  }
  let letters = (raw.toUpperCase().match(/\b[A-F]\b/g) || []).filter((l) => valid.includes(l));
  if (!letters.length) {
    // options numbered 1..4 in the paper
    letters = (raw.match(/\b[1-6]\b/g) || []).map((d) => LETTERS[Number(d) - 1]).filter((l) => valid.includes(l));
  }
  letters = Array.from(new Set(letters));
  if (kind === 'multi') return letters;
  return letters.length ? letters[0] : '';
};

export const hasAnswer = (q, answer) => {
  const kind = kindOf(q);
  if (kind === 'subjective') return true; // written in the paper, so there is nothing to pick
  if (kind === 'matrix') return String(answer ?? '').trim() !== ''; // the matching has to be given
  if (kind === 'tf') return answer === 'true' || answer === 'false';
  if (kind === 'multi') return Array.isArray(answer) && answer.length > 0;
  if (kind === 'numeric') return /^-?\d+(\.\d+)?$/.test(String(answer ?? '').trim());
  return LETTERS.slice(0, optionCount(q)).includes(answer);
};

// A kept question must be one the import can actually save as a real question.
export const fixProblem = (q) => {
  if (!String(q.text || '').trim()) return 'The question text is empty';
  if (hasOptions(q) && optionCount(q) < 2) return 'It needs at least two options';
  if (q.question_type === 'matrix_match' && ((q.matrix_left || []).length < 2 || (q.matrix_right || []).length < 2)) {
    return 'A matching question needs both columns';
  }
  const s = q.secondary; // the Hindi version joined onto this English question
  if (s) {
    if (!String(s.text || '').trim()) return 'The Hindi version has no text';
    if (kindOf(s) !== kindOf(q)) return 'The English and Hindi versions have different types. Fix one, or press Split';
    if (hasOptions(q) && (s.options || []).length !== (q.options || []).length) {
      return 'The English and Hindi versions have a different number of options. Fix one, or press Split';
    }
  }
  return '';
};

// ---- the same question printed in English and in Hindi ---------------------------------
//
// Bilingual papers print each question twice. They are joined into one "unit" so the teacher
// sees, keeps and answers it once. Pairing is a careful guess: a wrong join is worse than a
// missed one, and the teacher can always Split a pair apart.

const normaliseNumber = (n) =>
  String(n ?? '').toLowerCase().replace(/^q(uestion)?\.?\s*/, '').replace(/[^a-z0-9]/g, '');

// What survives translation: numbers, LaTeX commands and variable letters inside maths.
const tokensOf = (text) => {
  const s = String(text ?? '');
  const tokens = new Set();
  (s.match(/\d+(?:\.\d+)?/g) || []).forEach((t) => tokens.add(t));
  (s.match(/\$\$?[^$]+\$\$?/g) || []).forEach((segment) => {
    (segment.match(/\\[a-zA-Z]+/g) || []).forEach((t) => tokens.add(t));
    (segment.replace(/\\[a-zA-Z]+/g, ' ').match(/[a-zA-Z]/g) || []).forEach((t) => tokens.add(t));
  });
  return tokens;
};

// null when either side has too little to judge (plain words, a single number).
const jaccard = (a, b) => {
  if (a.size < 2 || b.size < 2) return null;
  let shared = 0;
  a.forEach((t) => { if (b.has(t)) shared += 1; });
  return shared / (a.size + b.size - shared);
};

const optionTokens = (q) => {
  const all = new Set();
  (q.options || []).forEach((o) => tokensOf(o.text).forEach((t) => all.add(t)));
  return all;
};

// -1 means "not a pair". Otherwise a score; 4 or more is accepted.
const pairScore = (en, hi, distance) => {
  const kind = kindOf(en);
  if (kind !== kindOf(hi)) return -1;
  if (hasOptions(en)) {
    const n = (en.options || []).length;
    if (n < 2 || n !== (hi.options || []).length) return -1; // broken questions are never joined
  }
  const stem = jaccard(tokensOf(en.text), tokensOf(hi.text));
  const opts = hasOptions(en) ? jaccard(optionTokens(en), optionTokens(hi)) : null;
  if (stem !== null && stem < 0.25) return -1; // the maths differs: a different question
  if (opts !== null && opts < 0.4) return -1;
  let score = 0;
  const a = normaliseNumber(en.number);
  if (a && a === normaliseNumber(hi.number)) score += 3;
  if (distance === 1) score += 2;
  if (stem !== null && stem >= 0.5) score += 2;
  if (opts !== null && opts >= 0.6) score += 2;
  return score;
};

// { hindiQuestionId: englishQuestionId } for every confident pair.
export const findPairs = (questions) => {
  const candidates = [];
  questions.forEach((en, i) => {
    if (!isImportable(en) || en.passage_id || en.language !== 'en') return;
    questions.forEach((hi, j) => {
      if (!isImportable(hi) || hi.passage_id || hi.language !== 'hi') return;
      const score = pairScore(en, hi, Math.abs(i - j));
      if (score >= 4) candidates.push({ en, hi, score, distance: Math.abs(i - j) });
    });
  });
  candidates.sort((x, y) => y.score - x.score || x.distance - y.distance);
  const used = new Set();
  const pairs = {};
  candidates.forEach(({ en, hi }) => {
    if (used.has(en.id) || used.has(hi.id)) return;
    used.add(en.id);
    used.add(hi.id);
    pairs[hi.id] = en.id;
  });
  return pairs;
};

// Several PDFs can be read at once, and each question remembers which one it came from
// (`jobId`). A pair is only ever looked for inside one PDF, so question 4 of the Physics
// paper is never joined to question 4 of the Maths paper.
export const findPairsPerJob = (questions) => {
  const byJob = new Map();
  questions.forEach((q) => {
    const key = q.jobId ?? '';
    if (!byJob.has(key)) byJob.set(key, []);
    byJob.get(key).push(q);
  });
  const all = {};
  byJob.forEach((list) => Object.assign(all, findPairs(list)));
  return all;
};

// ---- passages the teacher changes -------------------------------------------------------
//
// The teacher can put a question under a passage (one read from the paper, or a new one they write), take one
// question out of its passage, or remove a passage and keep its questions as separate ones. Those choices sit on top
// of what was read: `links[questionId]` is the passage the question now belongs to (an id), or null for none, and
// `newPassages[id]` is the text of a passage the teacher wrote.
export const NEW_PASSAGE = 'new-passage-';
export const COMPREHENSIVE_TYPES = ['mcq_single', 'mcq_multi', 'numerical', 'true_false']; // what a passage can hold

export const applyPassageEdits = (questions, links = {}, newPassages = {}) => {
  const text = { ...newPassages };
  questions.forEach((q) => {
    if (q.passage_id && q.passage && !text[q.passage_id]) text[q.passage_id] = q.passage;
  });
  const edited = questions.map((q) => {
    if (!Object.prototype.hasOwnProperty.call(links, q.id)) return q;
    const id = links[q.id];
    return id ? { ...q, passage_id: id, passage: text[id] || '' } : { ...q, passage_id: null, passage: null };
  });
  // The questions of one passage sit together, where its first question is, so the passage is shown once above
  // them and goes to the test as one comprehension question.
  const placed = new Set();
  const ordered = [];
  edited.forEach((q) => {
    if (placed.has(q.id)) return;
    const group = q.passage_id ? edited.filter((m) => m.passage_id === q.passage_id) : [q];
    group.forEach((m) => {
      placed.add(m.id);
      ordered.push(m);
    });
  });
  return ordered;
};

// The passages a question can be put under: each one once, with a short label to pick it by.
export const passageChoices = (questions) => {
  const seen = new Map();
  questions.forEach((q) => {
    if (!q.passage_id || seen.has(q.passage_id)) return;
    const words = String(q.passage || '').replace(/\s+/g, ' ').trim();
    seen.set(q.passage_id, {
      id: q.passage_id,
      label: `Passage ${seen.size + 1}${words ? `: ${words.slice(0, 60)}${words.length > 60 ? '...' : ''}` : ''}`
    });
  });
  return Array.from(seen.values());
};

// The questions as the teacher sees them: a joined pair is one unit, the English question
// carrying its Hindi twin as `secondary`. Everything else is passed through unchanged.
export const buildUnits = (questions, pairs) => {
  const byId = new Map(questions.map((q) => [q.id, q]));
  const twinOf = {};
  Object.entries(pairs || {}).forEach(([hindiId, englishId]) => {
    if (byId.has(hindiId) && byId.has(englishId)) twinOf[englishId] = byId.get(hindiId);
  });
  const hidden = new Set(Object.values(twinOf).map((q) => q.id));
  return questions
    .filter((q) => !hidden.has(q.id))
    .map((q) => (twinOf[q.id] ? { ...q, secondary: twinOf[q.id] } : q));
};

export const LANGUAGE_MODES = [
  { value: 'both', label: 'Both languages' },
  { value: 'en', label: 'English only' },
  { value: 'hi', label: 'Hindi only' }
];

const squash = (t) => String(t ?? '').toLowerCase().replace(/\s+/g, '');

// The single question that goes into the test for a unit, in the chosen language(s).
// "Both" stacks English over Hindi; an option that reads the same in both (numbers, formulas)
// is shown once, otherwise as "English / Hindi".
export const composeQuestion = (unit, mode = 'both') => {
  const s = unit.secondary;
  if (!s || mode === 'en') {
    const { secondary, ...rest } = unit;
    return rest;
  }
  if (mode === 'hi') {
    return {
      ...s,
      topic: s.topic || unit.topic,
      difficulty: s.difficulty || unit.difficulty,
      figures: (s.figures || []).length ? s.figures : unit.figures
    };
  }
  const options = (unit.options || []).map((o, i) => {
    const other = (s.options || [])[i];
    return other && squash(o.text) !== squash(other.text) ? { ...o, text: `${o.text} / ${other.text}` } : o;
  });
  const solution = [unit.solution, s.solution]
    .filter(Boolean)
    .filter((v, i, all) => all.findIndex((x) => squash(x) === squash(v)) === i)
    .join('\n\n');
  const { secondary, ...rest } = unit;
  return {
    ...rest,
    text: `${unit.text}\n\n${s.text}`,
    options,
    solution: solution || null,
    figures: (unit.figures || []).length ? unit.figures : (s.figures || [])
  };
};

// A written question has no answer field of its own in a test, only a solution, so its answer is the solution's
// last paragraph ("Final answer: ..."), as the scan service writes it. An answer left as it came keeps the
// solution as it is; a changed one replaces that paragraph; a cleared one removes it.
const FINAL_PARAGRAPH = '(^|\\n\\s*\\n)(Final answer|अंतिम उत्तर):[\\s\\S]*?(?=\\n\\s*\\n|$)';

// A short answer whose inline math was opened with $ and never closed shows as raw LaTeX: close it at the end.
// Escaped dollars (\$) and display math ($$...$$) are left alone; only a lone, unpaired $ counts.
export const closeMath = (value) => {
  const text = String(value ?? '');
  const bare = text.replace(/\\\$/g, '').replace(/\$\$[\s\S]+?\$\$/g, '');
  return (bare.match(/\$/g) || []).length % 2 ? `${text}$` : text;
};

export const solutionWithAnswer = (solution, answer, original, hindi) => {
  const text = String(solution ?? '');
  const typed = String(answer ?? '').trim();
  const hasFinal = new RegExp(FINAL_PARAGRAPH).test(text);
  if (typed === String(original ?? '').trim() && (!typed || hasFinal)) {
    return text.replace(new RegExp(FINAL_PARAGRAPH, 'g'), (paragraph) => closeMath(paragraph));
  }
  const base = text.replace(new RegExp(FINAL_PARAGRAPH, 'g'), '').trim();
  if (!typed) return base;
  const line = `${hindi ? 'अंतिम उत्तर' : 'Final answer'}: ${closeMath(typed)}`;
  return base ? `${base}\n\n${line}` : line;
};

// Drop an answer that no longer points at an option (after the teacher removes one).
export const sanitizeAnswer = (q, answer) => {
  const kind = kindOf(q);
  if (kind === 'subjective' || kind === 'matrix') return typeof answer === 'string' ? answer : ''; // typed, kept as typed
  if (kind === 'tf') return answer === 'true' || answer === 'false' ? answer : '';
  if (kind === 'numeric') return typeof answer === 'string' ? answer : '';
  const valid = LETTERS.slice(0, optionCount(q));
  // The teacher can switch a question between single and multiple correct, so carry the answer across.
  if (kind === 'multi') {
    const list = Array.isArray(answer) ? answer : answer ? [answer] : [];
    return list.filter((l) => valid.includes(l));
  }
  const one = Array.isArray(answer) ? (answer.length === 1 ? answer[0] : '') : answer;
  return valid.includes(one) ? one : '';
};

// Questions under one passage go to the test as one comprehension question, and the question bank wants a single
// type across it, so they must all be the same kind and the passage must have text.
export const groupProblem = (members) => {
  if (!members || members.length === 0) return '';
  if (new Set(members.map(kindOf)).size > 1) {
    return 'The questions under one passage must all be the same type. Make their types match, or leave some out.';
  }
  if (!String(members[0].passage || '').trim()) return 'The passage is empty. Use Edit passage to add it.';
  return '';
};

// The kept questions under each passage, by passage id, in order.
export const keptPassageGroups = (questions, keep, kind = 'objective') => {
  const groups = new Map();
  if (kind === 'subjective') return groups;
  questions.forEach((q) => {
    // A written question under a passage goes in on its own: a comprehension question holds objective parts only.
    if (!q.passage_id || isWritten(q) || !isImportable(q, kind) || !keep[q.id]) return;
    if (!groups.has(q.passage_id)) groups.set(q.passage_id, []);
    groups.get(q.passage_id).push(q);
  });
  return groups;
};

export const reviewStatus = (questions, keep, answers, kind = 'objective') => {
  let kept = 0;
  let needAnswer = 0;
  let needFix = 0;
  let firstBlockingId = null;
  questions.forEach((q) => {
    if (!isImportable(q, kind) || !keep[q.id]) return;
    kept += 1;
    const blocked = !hasAnswer(q, answers[q.id]) || fixProblem(q);
    if (!hasAnswer(q, answers[q.id])) needAnswer += 1;
    if (fixProblem(q)) needFix += 1;
    if (blocked && firstBlockingId === null) firstBlockingId = q.id;
  });
  keptPassageGroups(questions, keep, kind).forEach((members) => {
    if (groupProblem(members)) {
      needFix += 1;
      if (firstBlockingId === null) firstBlockingId = members[0].id;
    }
  });
  return {
    found: questions.length,
    importable: questions.filter((q) => isImportable(q, kind)).length,
    kept,
    needAnswer,
    needFix,
    needReview: questions.filter((q) => isImportable(q, kind) && keep[q.id] && q.needs_review).length,
    firstBlockingId,
    ready: kept > 0 && needAnswer === 0 && needFix === 0
  };
};

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// ---- marks by pattern ----------------------------------------------------------------------
//
// The exam patterns have fixed marking, so the teacher does not type it. Marks are kept per
// question type, the same way the Aulas AI generator reports them (marking_scheme_by_type), and the
// existing import reads them from there. Partial marking is not a per-question mark: it belongs to
// the marking scheme chosen on the Create Test screen.
const patternKey = (pattern) => String(pattern ?? '').toLowerCase().replace(/[^a-z]/g, '');

const flat = (correct, incorrect) => ({
  true_false: { correct, incorrect },
  single_mcq: { correct, incorrect },
  multi_correct_mcq: { correct, incorrect },
  integer_type: { correct, incorrect }
});

const PATTERN_MARKING = {
  jeemains: { title: 'JEE Mains', byType: flat(4, 1) },
  jeemain: { title: 'JEE Mains', byType: flat(4, 1) },
  neet: { title: 'NEET', byType: flat(4, 1) },
  jeeadvanced: {
    title: 'JEE Advanced',
    partial: true, // multiple-correct partial credit comes from the marking scheme, not from here
    byType: {
      true_false: { correct: 3, incorrect: 1 }, // true/false follows the single-correct rule
      single_mcq: { correct: 3, incorrect: 1 },
      multi_correct_mcq: { correct: 4, incorrect: 1 },
      integer_type: { correct: 4, incorrect: 1 }
    }
  },
  // Banking marks depend on the stage (the class). Prelims is +1 / -0.25 in IBPS and SBI alike. Mains marks
  // differ by exam and section, so the teacher types the marks for a correct answer; a wrong answer always
  // loses a quarter of them.
  banking: {
    byClass: {
      prelims: { title: 'Banking Prelims', byType: flat(1, 0.25) },
      mains: { title: 'Banking Mains', negativeShare: 0.25 }
    }
  }
};

// null for a pattern (or a stage of it) with no fixed rule, where the teacher types the marks as before.
// A rule with byType fixes every mark; a rule with negativeShare fixes only the wrong-answer marks.
export const patternMarkingRule = (pattern, cls) => {
  const entry = PATTERN_MARKING[patternKey(pattern)];
  if (!entry) return null;
  if (!entry.byClass) return entry;
  return entry.byClass[patternKey(cls)] || null;
};

// The wrong-answer marks a negativeShare rule gives for the typed correct marks, to three decimals
// (a 1.5-mark question loses 0.375).
export const shareOf = (rule, correct) =>
  Math.round((Number(correct) || 0) * rule.negativeShare * 1000) / 1000;

// Marks per question type for the paper: the rule's fixed marks, or the typed marks for every type.
export const buildMarkingByType = (pattern, typed, cls) => {
  const rule = patternMarkingRule(pattern, cls);
  if (rule && rule.byType) return rule.byType;
  const correct = Number(typed && typed.correct) || 0;
  const incorrect = rule ? shareOf(rule, correct) : Math.abs(Number(typed && typed.incorrect) || 0);
  return flat(correct, incorrect);
};

// ---- which chapter a question is filed under -------------------------------------------------

const chapterKey = (name) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// The parser reads a topic, not the syllabus chapter. Use it when it names one of the chapters
// chosen for the question's subject. With several subjects a question must still land inside its
// own subject, so with no match it takes that subject's first chosen chapter. With one subject the
// unmatched topic is passed on as it always was, and the import tells the teacher what it re-filed.
export const pickChapter = (topic, subjectChapters, severalSubjects) => {
  const names = subjectChapters || [];
  const key = chapterKey(topic);
  if (key) {
    const exact = names.find((n) => chapterKey(n) === key);
    if (exact) return exact;
    const loose = names.find((n) => {
      const other = chapterKey(n);
      return other && (other.includes(key) || key.includes(other));
    });
    if (loose) return loose;
  }
  if (severalSubjects && names.length > 0) return names[0];
  return topic || '';
};

// ---- does the question belong where the teacher put it? -------------------------------------
//
// The scan service labels every question with the subject it really seems to belong to and a
// chapter from the institute's own list. That is compared with what the teacher chose, because a
// question filed under the wrong subject or chapter would be a problem for the question bank later.
// A mismatch is a warning, never a block: the teacher looks, and may carry on with their own choice.

const nameKey = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9\u0900-\u097f]/g, '');

// "Maths" and "Mathematics" are the same subject; "Physics" and "Chemistry" are not.
export const sameSubject = (a, b) => {
  const x = nameKey(a);
  const y = nameKey(b);
  if (!x || !y) return false;
  if (x === y) return true;
  // Same first four letters is the same subject: Maths / Mathematics, Chem / Chemistry.
  return Math.min(x.length, y.length) >= 4 && x.slice(0, 4) === y.slice(0, 4);
};

// A PDF can hold every subject of the test (one paper with a Physics and a Chemistry section). It is tagged MIXED,
// and each of its questions goes to the subject the scan read for it, among those chosen. One the scan could not
// place goes to the first chosen subject, where the subject check flags it.
export const MIXED = '__all_subjects__';
export const subjectFor = (questionSubject, pdfSubject, chosen, label = (v) => v) => {
  if (pdfSubject !== MIXED) return pdfSubject;
  const list = chosen || [];
  return list.find((v) => sameSubject(questionSubject, label(v)) || sameSubject(questionSubject, v)) || list[0] || '';
};

const hasName = (names, name) => (names || []).some((n) => nameKey(n) === nameKey(name));

// status: 'ok' | 'subject' | 'chapter' | 'unsure' | 'unchecked'
//   subject   the service thinks it is another subject than the PDF was tagged with
//   chapter   the service names a chapter, but not one the teacher chose. offList: no chapter on the list fitted,
//             so the name is the service's own (suggested_chapter), and the question is filed there unless the
//             teacher keeps their choice
//   unsure    the service could not place it in any chapter
//   unchecked the service sent no labels (an older service), so nothing can be said
// `taggedSubject` is the subject the PDF was tagged with; `chosenChapters` are the ones ticked for it.
export const checkQuestion = (unit, { taggedSubject, chosenChapters }) => {
  const labelled =
    Object.prototype.hasOwnProperty.call(unit, 'chapter') && Boolean(unit.subject || unit.chapter || unit.topic);
  if (!labelled) return { status: 'unchecked' };
  if (unit.subject && taggedSubject && !sameSubject(unit.subject, taggedSubject)) {
    return { status: 'subject', suggestedSubject: unit.subject, suggestedChapter: unit.chapter || unit.suggested_chapter || '' };
  }
  if (!(chosenChapters || []).length) return { status: 'unchecked' };
  if (!unit.chapter && unit.suggested_chapter) {
    if (hasName(chosenChapters, unit.suggested_chapter)) return { status: 'ok', suggestedChapter: unit.suggested_chapter };
    return { status: 'chapter', suggestedChapter: unit.suggested_chapter, offList: true };
  }
  if (!unit.chapter) return { status: 'unsure' };
  if (hasName(chosenChapters, unit.chapter)) return { status: 'ok', suggestedChapter: unit.chapter };
  return { status: 'chapter', suggestedChapter: unit.chapter };
};

export const needsLook = (check) => Boolean(check) && ['subject', 'chapter', 'unsure'].includes(check.status);

// The chapter a question is filed under. The teacher's own pick wins. A question that matches what the
// teacher chose goes under the chapter the service named. Anything flagged stays under the teacher's
// choice, whether or not the service agrees (the topic is used when it names one of their chapters).
export const chapterFor = ({ topic, chapter }, check, override, chosenChapters, severalSubjects) => {
  const names = chosenChapters || [];
  if (override && hasName(names, override)) return names.find((n) => nameKey(n) === nameKey(override));
  if (check && check.status === 'ok') return pickChapter(chapter, names, severalSubjects);
  return pickChapter(topic, names, severalSubjects);
};

// The paper in the exact shape the Aulas AI screen hands to startAiTestHandoff.
// extras: { markingByType, chaptersBySubject: { subject: [chapter names] }, subjectLabel(value),
//           checks: { questionId: check }, overrides: { questionId: chapter the teacher picked },
//           testKind: 'objective' | 'subjective' }
const MARK_KEY = { single: 'single_mcq', multi: 'multi_correct_mcq', numeric: 'integer_type', tf: 'true_false', matrix: 'single_mcq' };

const optionTexts = (q) => (hasOptions(q) ? (q.options || []).slice(0, LETTERS.length).map((o) => o.text) : []);
const answerText = (kind, answer) => (kind === 'multi' ? [...answer].sort().join('') : String(answer ?? '').trim());
const figureOf = (q) => {
  const first = (q.figures || [])[0];
  return first ? { required: true, rendered_url: first.url, absolute_url: figureSrc(first) } : null;
};

export const buildGeneratedTest = (items, marking, extras = {}) => {
  const {
    markingByType, chaptersBySubject = {}, subjectLabel = (v) => v, checks = {}, overrides = {}, testKind = 'objective',
    subjectiveMarks = null // a hybrid paper: { correct, incorrect } for each written question
  } = extras;
  const subjective = testKind === 'subjective';
  const hybrid = testKind === 'hybrid';
  const bySubject = new Map();

  // Questions under one passage become one comprehension question, placed where its first question was.
  const entries = [];
  const groups = new Map();
  items.forEach((item) => {
    const pid = item.q.passage_id;
    if (pid && !subjective && !isWritten(item.q)) {
      if (!groups.has(pid)) {
        const group = { members: [] };
        groups.set(pid, group);
        entries.push({ group });
      }
      groups.get(pid).members.push(item);
    } else {
      entries.push({ item });
    }
  });

  const marksOf = (q) => {
    const rule = markingByType && markingByType[MARK_KEY[kindOf(q)]];
    return {
      marks: rule ? rule.correct : (marking && marking.correct) || 0,
      negative: Math.abs(rule ? rule.incorrect : (marking && marking.incorrect) || 0)
    };
  };

  const questions = entries.map((entry, index) => {
    const lead = entry.group ? entry.group.members[0].q : entry.item.q;
    const subject = lead.subjectValue || '';
    const chapterOf = (unit, q) =>
      chapterFor({ topic: q.topic, chapter: unit.chapter }, checks[unit.id], overrides[unit.id], chaptersBySubject[subject], true);
    let out;
    if (entry.group) {
      const subs = entry.group.members.map(({ q: unit, answer, mode }) => {
        const q = composeQuestion(unit, mode);
        const kind = kindOf(q);
        const m = marksOf(q);
        const sub = {
          question: q.text,
          type: AI_TYPE[q.question_type],
          question_type: AI_TYPE[q.question_type],
          options: optionTexts(q),
          correct_answer: answerText(kind, answer),
          explanation: q.solution || '',
          marks: m.marks,
          negative_marks: m.negative
        };
        const diagram = figureOf(q);
        if (diagram) sub.diagram = diagram;
        return sub;
      });
      out = {
        id: index + 1,
        type: 'comprehensive',
        question_type: 'comprehensive',
        question: '',
        paragraph: lead.passage || '',
        sub_questions: subs,
        options: [],
        correct_answer: '',
        explanation: '',
        chapter: chapterOf(lead, composeQuestion(lead, 'both')),
        topic: lead.topic || '',
        difficulty: capitalise(lead.difficulty)
      };
    } else {
      const { q: unit, answer, mode } = entry.item;
      const q = composeQuestion(unit, mode); // a joined Hindi + English pair becomes one question
      const kind = kindOf(q);
      const written = subjective || (hybrid && kind === 'subjective');
      const typeKey = written ? 'subjective' : AI_TYPE[q.question_type];
      out = {
        id: index + 1,
        question: q.text,
        type: typeKey,
        question_type: typeKey,
        options: written ? [] : optionTexts(q),
        correct_answer: written ? '' : answerText(kind, answer),
        explanation: kind === 'subjective'
          ? solutionWithAnswer(q.solution, answer, q.answer, mode === 'hi' || q.language === 'hi')
          : q.solution || '',
        chapter: chapterOf(unit, q),
        topic: q.topic || '',
        difficulty: capitalise(q.difficulty)
      };
      // A subjective question carries no marks of its own: they come from the marking scheme on the next screen.
      if (subjective) {
        out.marks = 0;
        out.negative_marks = 0;
      } else if (written) {
        // In a hybrid paper a written question takes the marks typed for written questions; negative is optional.
        out.marks = Number(subjectiveMarks && subjectiveMarks.correct) || 0;
        out.negative_marks = Math.abs(Number(subjectiveMarks && subjectiveMarks.incorrect) || 0);
      }
      const diagram = figureOf(q);
      if (diagram) out.diagram = diagram;
      // A matching question goes in the way Aulas AI sends one: both columns, printed under the statement, and its
      // printed choices as options (or, with none, the correct matching as the one option).
      if (q.question_type === 'matrix_match') {
        out.column_a = (q.matrix_left || []).map((m) => `(${m.label}) ${m.text}`);
        out.column_b = (q.matrix_right || []).map((m) => `(${m.label}) ${m.text}`);
      }
    }
    if (subject) {
      if (!bySubject.has(subject)) bySubject.set(subject, []);
      bySubject.get(subject).push(index + 1);
    }
    return out;
  });
  const paper = { questions, totalQuestions: questions.length, marking_scheme: subjective ? null : marking };
  if (markingByType && !subjective) paper.marking_scheme_by_type = markingByType;
  if (bySubject.size > 0) {
    paper.sections = Array.from(bySubject.entries()).map(([subject, ids]) => ({
      subject: subjectLabel(subject),
      question_ids: ids
    }));
  }
  return paper;
};

const dominantDifficulty = (items) => {
  const counts = {};
  items.forEach(({ q }) => {
    if (q.difficulty) counts[q.difficulty] = (counts[q.difficulty] || 0) + 1;
  });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return top ? capitalise(top[0]) : 'Medium';
};

// What the hand-off reads from the generation form: subject for the lookup, the
// chapter names to match, and the types the teacher "ticked". Types are the ones
// actually present, because a single ticked type overrides each question's own label.
// Chapters of both classes, one set per class, in class order (the same grouping the Aulas AI form uses).
export const groupChaptersByClass = (list) => {
  const map = new Map();
  list.forEach((c) => {
    const key = c.class_ || 'Other';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => String(a).localeCompare(String(b), undefined, { numeric: true }))
    .map(([classKey, chapters]) => ({
      classKey,
      className: String(classKey).toLowerCase().startsWith('class') ? classKey : `Class ${classKey}`,
      chapters
    }));
};

// A ticked chapter is remembered with its subject, because two subjects can have a chapter of the same name
// (Thermodynamics in Physics and in Chemistry) and ticking one must not tick the other.
const CHAPTER_SEP = '::';
export const chapterId = (subject, name) => `${subject}${CHAPTER_SEP}${name}`;
export const chapterName = (id) => {
  const s = String(id);
  const at = s.indexOf(CHAPTER_SEP);
  return at === -1 ? s : s.slice(at + CHAPTER_SEP.length);
};
export const chapterSubject = (id) => {
  const s = String(id);
  const at = s.indexOf(CHAPTER_SEP);
  return at === -1 ? '' : s.slice(0, at);
};
// The names ticked for one subject, or for every subject when `subject` is not given.
export const tickedNames = (ids, subject) => {
  const names = (ids || []).filter((id) => subject === undefined || chapterSubject(id) === subject).map(chapterName);
  return Array.from(new Set(names));
};
// A review saved before chapters carried their subject holds bare names: give each one every chosen subject, and
// the chapters that subject does not have are dropped once its chapter list is read.
export const withSubjects = (ids, subjects) =>
  Array.from(new Set((ids || []).flatMap((id) => (chapterSubject(id) ? [id] : (subjects || []).map((s) => chapterId(s, id))))));

// What the Create Test screen calls the syllabus: one chapter, a part of the subject, or all of it.
export const syllabusFor = (ticked, offered) => {
  if (ticked.length <= 1) return 'Chapter';
  return offered.length > 0 && offered.every((id) => ticked.includes(id)) ? 'Full' : 'Part';
};

export const buildSelection = (form, items) => ({
  // Says the paper is from Scan PDF, so Create Test takes its test type and syllabus as sent (the Aulas AI generator's
  // hand-off keeps its own defaults).
  source: 'scanPdf',
  // Create Test calls a test holding both kinds 'Mixed'.
  testType: { subjective: 'Subjective', hybrid: 'Mixed' }[form.testKind] || 'Objective',
  title: form.title ? String(form.title).trim() : '',
  pattern: form.pattern,
  class: form.class,
  subject: form.subject,
  chapters: form.chapters,
  ...(form.syllabus ? { syllabus: form.syllabus } : {}),
  // What the shared hand-off reads to make a Full test.
  fullSyllabus: form.syllabus === 'Full',
  n_questions: String(items.length),
  difficulty: dominantDifficulty(items),
  question_types: (() => {
    const subjective = form.testKind === 'subjective';
    const types = new Set(items.map(({ q }) => (subjective || isWritten(q) ? 'subjective' : AI_TYPE[q.question_type])));
    if (!subjective && items.some(({ q }) => q.passage_id && !isWritten(q))) types.add('comprehensive');
    return Array.from(types);
  })(),
  total_marks: '',
  duration_mins: ''
});

export const buildMarking = (correct, incorrect) => ({
  correct: Number(correct) || 0,
  incorrect: -Math.abs(Number(incorrect) || 0),
  unattempted: 0
});
